import { createInternalCategory, updateInternalCategory, useInternalCategory } from '@gozoki/api'
import { useInternalCategoryForm } from '@gozoki/api/src/forms/useInternalCategoryForm'
import { Button, IconButton, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useEffect, type FormEvent } from 'react'
import z from 'zod/v3'
import LoadingPaper from '../../../components/containers/LoadingPaper'
import TextFieldRhf from '../../../components/inputs/TextFieldRhf'
import Page from '../../../components/Page'
import { useRights } from '../../../utils/hooks'
import { ThemeColors } from '../../../utils/theme/colors.theme'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides } from '../../../utils/theme/overrides.theme'

export const Route = createFileRoute('/_authenticated/categories/internal/new')({
  component: AddInternalCategoryPage,
  validateSearch: z.object({
    internalCategoryId: z.number().optional(),
  }),
})

function AddInternalCategoryPage() {
  const navigate = Route.useNavigate()
  const { internalCategoryId } = Route.useSearch()

  const isUserAdmin = useRights(['ADMIN'])
  const updateMode = !!internalCategoryId // Update mode if there is a category ID in the URL

  // If this is update mode, use isLoading to show a loading indicator before the data arrives
  const { data, isLoading } = useInternalCategory(internalCategoryId)

  const { control, trigger, getValues, setValue } = useInternalCategoryForm()

  // Fill the form with initial data once the data is fetched
  useEffect(() => {
    if (data?.name) {
      setValue('name', data?.name)
    }
  }, [setValue, data?.name])

  const navigateBack = () => navigate({ search: { tab: 'internal' }, to: '/categories' })

  const { mutate: upload, isPending: isUploading } = useMutation({
    mutationFn: createInternalCategory,
    onSuccess: navigateBack,
  })

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, name }: { name: string; id: number }) =>
      updateInternalCategory(id, { name }),
    onSuccess: navigateBack,
  })

  const uploadCategory = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const valid = await trigger() // Trigger form validation

    if (valid) {
      upload(getValues())
    }
    // If invalid, the invalid fields will be outlined with red
  }

  const uploadAndUpdateCategory = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const valid = await trigger('name') // No need to check for image, which cannot be deleted

    if (valid && internalCategoryId) {
      update({ id: internalCategoryId, ...getValues() })
    }
  }

  return (
    <Page sx={{ margin: 'auto', maxWidth: '50vw' }}>
      <form onSubmit={updateMode ? uploadAndUpdateCategory : uploadCategory}>
        <div style={{ alignItems: 'center', display: 'flex' }}>
          <IconButton onClick={navigateBack}>
            <ArrowLeft
              style={{
                color: ThemeColors.BLACK,
                height: '24px',
                width: '24px',
              }}
            />
          </IconButton>
          <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
            {updateMode
              ? `${isUserAdmin ? 'Modifier' : 'Voir'} une catégorie interne`
              : `Ajouter une catégorie interne`}
          </Typography>

          <Button
            variant="outlined"
            sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
            onClick={navigateBack}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isUserAdmin ? true : isUploading || isUpdating}
          >
            {updateMode ? 'Modifier' : 'Ajouter'}
          </Button>
        </div>
        <LoadingPaper
          sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
          loading={isLoading && updateMode}
        >
          <Typography mb="8px" style={fonts.inputHeader}>
            Nom
          </Typography>
          <TextFieldRhf
            variant="outlined"
            label="Nom de la catégorie"
            control={control}
            trigger={trigger}
            name="name"
            fullWidth
          />
        </LoadingPaper>
      </form>
    </Page>
  )
}
